import { generatePath, matchPath } from 'react-router-dom';

interface BackRouteRule {
  /** Route patterns of one page — its tabs included. First matching rule wins. */
  patterns: string[];
  /** The page itself: every tab of it shares this key (a visit spans its tabs). */
  page: string;
  /** Hierarchical parent opened by back on a direct entry; null — no back. */
  parent: string | null;
}

/** Order matters: literal segments before the `:param` patterns they overlap. */
const BACK_ROUTE_RULES: BackRouteRule[] = [
  { patterns: ['/dashboard'], page: '/dashboard', parent: null },
  { patterns: ['/tournaments', '/tournaments/public', '/tournaments/archived'], page: '/tournaments', parent: '/dashboard' },
  { patterns: ['/tournaments/create'], page: '/tournaments/create', parent: '/tournaments' },
  {
    patterns: ['/tournaments/:id', '/tournaments/:id/participants', '/tournaments/:id/leaderboard', '/tournaments/:id/settings'],
    page: '/tournaments/:id',
    parent: '/tournaments',
  },
  {
    patterns: ['/tournaments/:id/matches/:matchId', '/tournaments/:id/matches/:matchId/predictions'],
    page: '/tournaments/:id/matches/:matchId',
    parent: '/tournaments/:id',
  },
  {
    patterns: ['/tournaments/:id/leaderboard/:userId'],
    page: '/tournaments/:id/leaderboard/:userId',
    parent: '/tournaments/:id/leaderboard',
  },
  { patterns: ['/matches', '/matches/leagues'], page: '/matches', parent: '/dashboard' },
  {
    patterns: [
      '/matches/:leagueId',
      '/matches/:leagueId/standings',
      '/matches/:leagueId/playoff',
      '/matches/:leagueId/tournaments',
    ],
    page: '/matches/:leagueId',
    parent: '/matches',
  },
  { patterns: ['/match/:matchId', '/match/:matchId/tournaments'], page: '/match/:matchId', parent: '/matches' },
  { patterns: ['/account/*'], page: '/account', parent: '/dashboard' },
  { patterns: ['/admin/*'], page: '/admin', parent: '/dashboard' },
];

export interface BackRoute {
  /** Path of the page with its tab segment stripped. */
  pageKey: string;
  /** Path back opens on a direct entry; null — the page has no back (burger). */
  parent: string | null;
}

/**
 * Back target of an AppLayout page. Pages outside the table (headless invite,
 * join, verify-email, account-blocked, not found) have no back — their own
 * buttons lead on, and a guard may bounce a fallback straight back.
 */
export function resolveBackRoute(pathname: string): BackRoute {
  for (const rule of BACK_ROUTE_RULES) {
    for (const pattern of rule.patterns) {
      const match = matchPath(pattern, pathname);
      if (match) {
        return {
          pageKey: generatePath(rule.page, match.params),
          parent: rule.parent && generatePath(rule.parent, match.params),
        };
      }
    }
  }
  return { pageKey: pathname, parent: null };
}
